import { AppBar, Box, Container, Drawer, IconButton, Typography } from "@mui/material";

import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import MenuIcon from "@mui/icons-material/Menu";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { routes } from "../../constants/routes";
import { CartContext } from "../../context/CartContext";
import { useGradient } from "../../utils/useGradient";
import { AccountAvatar } from "../utils/AccountAvatar";
import Button, { ButtonVariant } from "../utils/Button";
import CartButton from "../utils/CartButton";
import { LanguageFlagButton } from "../utils/LanguageSelector";
import NavigationPanel from "../utils/NavigationPanel";
import SearchButton from "../utils/SearchButton";
import Logo from "./Logo";

const Header = () => {
  const [mobileNavOpen, setMobileNavOpen] = useState<boolean>(false);
  const { t } = useTranslation();
  const { cart } = useContext(CartContext);
  const gradient = useGradient("horizontal");

  const navigation = t("header.navigation");
  const search = t("header.search");
  const account = t("header.account");

  return (
    <AppBar
      position="fixed"
      sx={{
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        backgroundColor: "transparent",
        boxShadow: "none",
        backgroundImage: "none"
      }}>
      <Container
        maxWidth="xl"
        sx={{
          width: "100%",
          mx: { xs: 2, lg: 4 },
          p: { xs: 3, lg: 5 },
          color: "white",
          background: gradient,
          filter: "drop-shadow(-4px 5px 4px rgba(0, 0, 0, 0.25))",
          borderBottomLeftRadius: { xs: 50, lg: 80 },
          borderBottomRightRadius: { xs: 15, lg: 30 }
        }}>
        {/* Mobile */}
        <Box
          display={{ xs: "flex", lg: "none" }}
          justifyContent="space-between"
          alignItems="center">
          <Link
            to={routes.home}
            style={{
              textDecoration: "none",
              color: "inherit",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              width: "70%"
            }}>
            <Logo variant="header" />
          </Link>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              ml: "auto",
              whiteSpace: "nowrap",
              gap: 2
            }}>
            <LanguageFlagButton />
            <Button
              onClick={() => setMobileNavOpen(true)}
              startIcon={<MenuIcon />}
              variant={ButtonVariant.accent}
              aria-label={
                cart?.length > 0 ? `${navigation} (${cart.length})` : (navigation as string)
              }>
              {cart?.length > 0 && (
                <Box
                  aria-hidden="true"
                  sx={(theme) => ({
                    position: "absolute",
                    borderRadius: "100%",
                    width: theme.spacing(5),
                    height: theme.spacing(5),
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    fontSize: "0.8rem",
                    fontWeight: "900",
                    top: theme.spacing(-2.5),
                    right: theme.spacing(-2.5),
                    backgroundColor: "red",
                    color: "white"
                  })}>
                  {cart.length}
                </Box>
              )}
            </Button>
          </Box>

          <Drawer
            anchor="right"
            open={mobileNavOpen}
            onClose={() => setMobileNavOpen(false)}
            aria-label={navigation}
            sx={{
              "& .MuiPaper-root, .MuiBackdrop-root": {
                width: "80%",
                borderTopRightRadius: "0",
                borderBottomRightRadius: "0"
              }
            }}>
            <Box
              sx={{
                position: "absolute",
                top: 20,
                right: 15,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-end"
              }}>
              <IconButton
                onClick={() => setMobileNavOpen(false)}
                aria-label={t("buttons.closeMenu")}>
                <CloseOutlinedIcon />
              </IconButton>
            </Box>
            <Box
              sx={{
                p: 4,
                minHeight: 1,
                display: "flex",
                flexDirection: "column"
              }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  mb: 1,
                  gap: 3,
                  "& .": {
                    width: "100%"
                  }
                }}>
                <Box>
                  <Typography variant="h5" mb={3}>
                    {navigation}
                  </Typography>
                  <NavigationPanel mobile setMobileNavOpen={() => setMobileNavOpen(false)} />
                  <Box mt={2}>
                    <CartButton mobile setMobileNavOpen={() => setMobileNavOpen(false)} />
                  </Box>
                </Box>

                <Box>
                  <Typography variant="h5" mb={3}>
                    {search}
                  </Typography>
                  <SearchButton mobile setMobileNavOpen={() => setMobileNavOpen(false)} />
                </Box>

                <Box>
                  <Typography variant="h5" mb={3}>
                    {account}
                  </Typography>
                  <AccountAvatar mobile setMobileNavOpen={() => setMobileNavOpen(false)} />
                </Box>
              </Box>
            </Box>
          </Drawer>
        </Box>

        {/* Desktop */}
        <Box
          sx={{
            display: { xs: "none", lg: "flex" },
            alignItems: "center"
          }}>
          <Link
            to={routes.home}
            style={{
              textDecoration: "none",
              color: "inherit",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center"
            }}>
            <Logo variant="header" />
          </Link>

          <Box
            component="nav"
            aria-label={navigation}
            sx={{
              display: { xs: "none", lg: "flex" },
              alignItems: "center",
              ml: 10,
              whiteSpace: "nowrap"
            }}>
            <NavigationPanel />
          </Box>
          <Box
            sx={{
              display: { xs: "none", lg: "flex" },
              alignItems: "center",
              ml: "auto",
              whiteSpace: "nowrap",
              gap: 2
            }}>
            <SearchButton />
            <LanguageFlagButton />
            <CartButton />
            <AccountAvatar />
          </Box>
        </Box>
      </Container>
    </AppBar>
  );
};

export default Header;
